import React from 'react';
import {
  Typography,
  Box,
  Paper,
  Avatar,
  Stack
} from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import RepeatIcon from '@mui/icons-material/Repeat';
import VerifiedIcon from '@mui/icons-material/Verified';
import FacebookIcon from '@mui/icons-material/Facebook';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import { PlatformPostProps } from './types';
import { SocialMediaImage, formatNumber, getInitials } from './SocialMediaPost';

export const TwitterPost: React.FC<PlatformPostProps> = ({ 
  content, 
  sender, 
  displayImageUrl, 
  altText, 
  replies7d, 
  likes7d 
}) => (
  <Paper elevation={0} sx={{ 
    bgcolor: (theme) => theme.palette.mode === 'dark' ? '#15202b' : '#ffffff',
    borderRadius: 2,
    border: (theme) => `1px solid ${theme.palette.mode === 'dark' ? '#2f3336' : '#eff3f4'}`,
    p: { xs: 1.5, sm: 2 },
    overflow: 'hidden',
    maxWidth: '100%' 
  }}>
    {/* Header */}
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
      <Avatar sx={{ 
        bgcolor: 'primary.main', 
        width: { xs: 40, sm: 48 }, 
        height: { xs: 40, sm: 48 },
        flexShrink: 0 
      }}>
        {getInitials(sender || '')}
      </Avatar>
      
      <Box sx={{ ml: { xs: 1, sm: 1.5 }, flex: 1, minWidth: 0, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              fontWeight: 'bold', 
              mr: 0.5, 
              color: (theme) => theme.palette.mode === 'dark' ? '#e7e9ea' : 'inherit',
              fontSize: { xs: '0.9rem', sm: '1rem' },
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: { xs: '120px', sm: 'none' }
            }}
          >
            {sender || 'Anonymous'}
          </Typography>
          <VerifiedIcon sx={{ fontSize: { xs: 14, sm: 16 }, color: '#1DA1F2', ml: 0.5, flexShrink: 0 }} />
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ 
              ml: 0.5,
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              display: { xs: 'none', sm: 'inline' }
            }}
          >
            @{sender?.replace(/\s+/g, '').toLowerCase() || 'anonymous'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mx: 0.5, display: { xs: 'none', sm: 'inline' } }}>·</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
            1h
          </Typography>
        </Box>
      </Box>
      <Box sx={{ flexShrink: 0 }}>
        <MoreHorizIcon sx={{ color: 'text.secondary', fontSize: { xs: 20, sm: 24 } }} />
      </Box>
    </Box>

    {/* Content */}
    <Typography 
      variant="body1" 
      sx={{ 
        whiteSpace: 'pre-wrap', 
        mb: displayImageUrl ? 2 : 0,
        fontSize: { xs: '0.875rem', sm: '0.95rem' },
        lineHeight: 1.4,
        color: (theme) => theme.palette.mode === 'dark' ? '#e7e9ea' : 'inherit',
        wordBreak: 'break-word',
        overflowWrap: 'break-word'
      }}
    >
      {content}
    </Typography>

    {/* Image */}
    {displayImageUrl && (
      <SocialMediaImage 
        src={displayImageUrl} 
        alt={altText}
        platform="twitter"
      />
    )}

    {/* Engagement stats */}
    <Stack 
      direction="row" 
      spacing={{ xs: 2, sm: 5 }} 
      justifyContent="space-between"
      sx={{ 
        mt: 2, 
        color: (theme) => theme.palette.mode === 'dark' ? '#8b98a5' : 'text.secondary', 
        px: { xs: 0, sm: 2 }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
        <ChatBubbleOutlineIcon sx={{ fontSize: { xs: 16, sm: 18 }, mr: 0.5, flexShrink: 0 }} />
        <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
          {formatNumber(replies7d)}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
        <RepeatIcon sx={{ fontSize: { xs: 16, sm: 18 }, mr: 0.5, flexShrink: 0 }} />
        <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
          {formatNumber(Math.round((likes7d || 0) * 0.25))}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
        <FavoriteBorderIcon sx={{ fontSize: { xs: 16, sm: 18 }, mr: 0.5, flexShrink: 0 }} />
        <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
          {formatNumber(likes7d)}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
        <ShareOutlinedIcon sx={{ fontSize: { xs: 16, sm: 18 }, flexShrink: 0 }} />
      </Box>
    </Stack>
  </Paper>
);

export const FacebookPost: React.FC<PlatformPostProps> = ({ 
  content, 
  sender, 
  displayImageUrl, 
  altText, 
  replies7d, 
  likes7d 
}) => (
  <Paper elevation={0} sx={{ 
    bgcolor: (theme) => theme.palette.mode === 'dark' ? '#242526' : '#ffffff',
    borderRadius: 2,
    border: (theme) => `1px solid ${theme.palette.mode === 'dark' ? '#3A3B3C' : '#dddfe2'}`,
    overflow: 'hidden'
  }}>
    {/* Header with blue bar */}
    <Box sx={{ display: 'flex', alignItems: 'center', p: 2, pb: 1 }}>
      <Avatar 
        sx={{ 
          width: 40, 
          height: 40,
          bgcolor: sender?.includes('Facebook') ? '#1877F2' : 'primary.main' 
        }}
      >
        {getInitials(sender || '')}
      </Avatar>
      
      <Box sx={{ ml: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: (theme) => theme.palette.mode === 'dark' ? '#E4E6EB' : '#050505' }}>
            {sender || 'Anonymous'}
          </Typography>
          {sender?.includes('Facebook') && 
            <VerifiedIcon sx={{ fontSize: 16, color: '#1877F2', ml: 0.5 }} />
          }
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="body2" sx={{ color: (theme) => theme.palette.mode === 'dark' ? '#B0B3B8' : 'text.secondary', fontSize: '0.8rem' }}>
            1h · <FacebookIcon sx={{ fontSize: 12, mb: '-2px' }} />
          </Typography>
        </Box>
      </Box>
      
      <Box sx={{ ml: 'auto' }}>
        <MoreHorizIcon sx={{ color: (theme) => theme.palette.mode === 'dark' ? '#B0B3B8' : '#65676b' }} />
      </Box>
    </Box>

    {/* Content */}
    <Box sx={{ px: 2, pb: 1 }}>
      <Typography 
        variant="body1" 
        sx={{ 
          whiteSpace: 'pre-wrap', 
          color: (theme) => theme.palette.mode === 'dark' ? '#E4E6EB' : '#050505',
          fontSize: '0.95rem',
          lineHeight: 1.4
        }}
      >
        {content}
      </Typography>
    </Box>

    {/* Image */}
    {displayImageUrl && (
      <SocialMediaImage 
        src={displayImageUrl} 
        alt={altText}
        platform="facebook"
      />
    )}

    {/* Engagement counts */}
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center',
      borderBottom: (theme) => `1px solid ${theme.palette.mode === 'dark' ? '#3A3B3C' : '#ced0d4'}`,
      px: 2,
      py: 1
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            width: 18, 
            height: 18, 
            borderRadius: '50%', 
            bgcolor: '#1877F2',
            mr: 0.5
          }}
        >
          <ThumbUpIcon sx={{ color: 'white', fontSize: 12 }} />
        </Box>
        <Typography variant="body2" sx={{ color: (theme) => theme.palette.mode === 'dark' ? '#B0B3B8' : '#65676b', fontSize: '0.9rem' }}>
          {formatNumber(likes7d)}
        </Typography>
      </Box>
      
      <Box sx={{ display: 'flex' }}>
        <Typography variant="body2" sx={{ color: (theme) => theme.palette.mode === 'dark' ? '#B0B3B8' : '#65676b', fontSize: '0.9rem', mr: 1.5 }}>
          {formatNumber(replies7d)} comments
        </Typography>
        <Typography variant="body2" sx={{ color: (theme) => theme.palette.mode === 'dark' ? '#B0B3B8' : '#65676b', fontSize: '0.9rem' }}>
          {formatNumber(Math.round((likes7d || 0) * 0.1))} shares
        </Typography>
      </Box>
    </Box>

    {/* Action buttons */}
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'space-around', 
      px: 1,
      py: 1
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 0.5, borderRadius: 1, '&:hover': { bgcolor: (theme) => theme.palette.mode === 'dark' ? '#3A3B3C' : '#f2f2f2' } }}>
        <ThumbUpOutlinedIcon sx={{ color: (theme) => theme.palette.mode === 'dark' ? '#B0B3B8' : '#65676b', fontSize: 18, mr: 1 }} />
        <Typography variant="body2" sx={{ color: (theme) => theme.palette.mode === 'dark' ? '#B0B3B8' : '#65676b', fontWeight: 'medium' }}>
          Like
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 0.5, borderRadius: 1, '&:hover': { bgcolor: (theme) => theme.palette.mode === 'dark' ? '#3A3B3C' : '#f2f2f2' } }}>
        <ChatBubbleOutlineIcon sx={{ color: (theme) => theme.palette.mode === 'dark' ? '#B0B3B8' : '#65676b', fontSize: 18, mr: 1 }} />
        <Typography variant="body2" sx={{ color: (theme) => theme.palette.mode === 'dark' ? '#B0B3B8' : '#65676b', fontWeight: 'medium' }}>
          Comment
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 0.5, borderRadius: 1, '&:hover': { bgcolor: (theme) => theme.palette.mode === 'dark' ? '#3A3B3C' : '#f2f2f2' } }}>
        <ShareOutlinedIcon sx={{ color: (theme) => theme.palette.mode === 'dark' ? '#B0B3B8' : '#65676b', fontSize: 18, mr: 1 }} />
        <Typography variant="body2" sx={{ color: (theme) => theme.palette.mode === 'dark' ? '#B0B3B8' : '#65676b', fontWeight: 'medium' }}>
          Share
        </Typography>
      </Box>
    </Box>
  </Paper>
);
